import { Grid, MenuItem, Select, SelectChangeEvent, Tab, Table, TableBody, Typography } from "@mui/material";
import * as api from "api/Core";
import { ChartJsHeight, ChartJsOptions, DefaultListParameters } from "assets/appConfig";
import { AppMessageBox } from "components/AppMessageBox";
import { AppTabs } from "components/AppTabs";
import { SpinnerIndicator } from "components/CallServer";
import MuiMessageBox from "components/MuiExtras/MessageBox/MuiMessageBox";
import MuiContainer from "components/MuiExtras/MuiContainer";
import MuiPagination from "components/MuiExtras/MuiPagination";
import MuiMuted from "components/MuiExtras/Typography/MuiMuted";
import LibUtils from "lib/LibUtils";
import { DashboardUserAndGroupsParticipationBaseFilter, DashboardUserAndGroupsParticipationKnowledgeDetailColumn, DashboardUserAndGroupsParticipationKnowledgeGraphModel, PagedResult } from "models/ApiModels";
import moment from "moment";
import * as React from "react";
import ChartComponent from "react-chartjs-2";
import { WithTranslation } from "react-i18next";
import { RenderTopicModelList, RenderTopicModelListEvents } from "views/Dashboards/components/RenderTopicModelList";
import { RenderTopicTestModelList } from "views/Dashboards/components/RenderTopicTestModelList";
import { RenderUserModelList, RenderUserModelListEvents } from "views/Dashboards/components/RenderUserModelList";
import { RenderUserTestModelList } from "views/Dashboards/components/RenderUserTestModelList";
import { ParticipationListPublicationPeriod } from "views/Dashboards/UserAndGroupsDashboard/Common";

/////////////////////////////////////////////////////////////////////////////////////

interface IGraphState {
    data: DashboardUserAndGroupsParticipationKnowledgeGraphModel[];
    loading: boolean;
}

export interface IGraphProps extends RenderUserModelListEvents, RenderTopicModelListEvents {
    filter: DashboardUserAndGroupsParticipationBaseFilter;
}

type GraphProps = IGraphProps & WithTranslation;

export class ParticipationKnowledgeTestGraph extends React.PureComponent<GraphProps, IGraphState>{

    constructor(props: GraphProps) {
        super(props);

        this.state = {
            data: null,
            loading: true,
        };
    }

    ///
    private loadData() {
        var de = moment().utc().endOf("day");
        var ds = moment().utc().startOf("day").add(-12, "months");

        api.getDashboardUserAndGroupsParticipationKnowledgeTest({
            detailColumn: DashboardUserAndGroupsParticipationKnowledgeDetailColumn.Graph,
            startDate: ds.toDate(),
            endDate: de.toDate(),
            range: null,
            ...this.props.filter
        })
            .then((result: DashboardUserAndGroupsParticipationKnowledgeGraphModel[]) => {
                result.forEach((v) => {
                    v.day = v.day.convertLocalToUtcDate();
                });

                this.setState({ data: result, loading: false });
            })
            .catch((error) => {
                this.setState({ data: null, loading: false });
            });
    }

    ///
    public componentDidMount() {
        this.loadData();
    }

    ///
    public componentDidUpdate(prevProps: GraphProps, prevState: IGraphState) {
        if (!LibUtils.isEqual(this.props.filter, prevProps.filter)) {
            this.loadData();
        }
    }

    ///
    private renderGraph() {
        const { data } = this.state;
        var dsb1: number[] = [];
        var dsb2: number[] = [];
        var dsline: number[] = [];
        var labels: string[] = [];
        var y = 0;

        data.forEach((v, index) => {
            var vday = moment(v.day);

            if (y != v.day.getFullYear()) {
                labels.push(vday.format("MMM/YYYY"));
                y = v.day.getFullYear();
            }
            else {
                labels.push(vday.format("MMM"));
            }

            dsb1.push(v.correct);
            dsb2.push(v.answered - v.correct);
            dsline.push(v.trustLevel);
        });


        return (
            <div style={{ position: "relative", height: ChartJsHeight, width: "100%" }}>
                <ChartComponent
                    type="line"
                    data={
                        {
                            labels: labels,
                            datasets: [
                                {
                                    type: "line",
                                    label: this.props.t("Views.Dashboards.UserAndGroups.KnowledgeTestGraph.DS1Label"),
                                    fill: false,
                                    backgroundColor: "#E6E600",
                                    borderColor: "#E6E600",
                                    data: dsline,
                                    xAxisID: 'lineX',
                                    yAxisID: 'lineY',
                                },
                                {
                                    type: "bar",
                                    label: this.props.t("Views.Dashboards.UserAndGroups.KnowledgeTestGraph.DS2Label"),
                                    backgroundColor: "green",
                                    data: dsb1
                                },
                                {
                                    type: "bar",
                                    label: this.props.t("Views.Dashboards.UserAndGroups.KnowledgeTestGraph.DS3Label"),
                                    backgroundColor: "red",
                                    data: dsb2
                                }
                            ]
                        }
                    }
                    options={
                        {
                            ...ChartJsOptions,

                            plugins: {
                                legend: {
                                    display: false
                                },

                                tooltip: {
                                    callbacks: {
                                        title: function (item: any) {
                                            return moment(data[item[0].dataIndex].day).format("MMM/YYYY");
                                        }
                                    }
                                },
                            },

                            scales: {
                                x: {
                                    display: true,
                                    stacked: true,
                                    grid: {
                                        display: false
                                    },
                                },

                                y: {
                                    display: true,
                                    stacked: true,
                                    title: {
                                        display: true,
                                        text: this.props.t("Views.Dashboards.UserAndGroups.KnowledgeTestGraph.YAxisLabel")
                                    },
                                    grid: {
                                        display: true
                                    },
                                    suggestedMin: 0
                                },

                                "lineX": {
                                    axis: "x",
                                    stacked: false,
                                    display: false, //optional
                                    min: 0,
                                    max: 100,
                                    beginAtZero: true,
                                },

                                "lineY": {
                                    axis: "y",
                                    stacked: false,
                                    display: true,
                                    position: "right",
                                    title: {
                                        display: true,
                                        text: this.props.t("Views.Dashboards.UserAndGroups.KnowledgeTestGraph.LineYAxisLabel")
                                    },
                                    beginAtZero: true,
                                    min: 0,
                                    max: 100
                                }
                            }
                        }
                    }
                />
            </div>
        );
    }

    ///
    public render() {
        const { data, loading } = this.state;

        if (loading)
            return <SpinnerIndicator />;

        if (data == null)
            return <AppMessageBox resource="LoadGraphDataFailed" />;

        return (
            <>
                <MuiContainer margin="dense" >
                    <Grid container justifyContent="space-between">
                        <Grid item>
                            <Typography component="p" variant="h6"><b>{this.props.t("Views.Dashboards.UserAndGroups.KnowledgeTestGraph.HeaderTitle").toString().toLocaleUpperCase()}</b></Typography>
                            <MuiMuted><small>
                                {this.props.t("Views.Dashboards.UserAndGroups.KnowledgeTestGraph.HeaderSubtitle")}
                            </small></MuiMuted>
                        </Grid>

                    </Grid>
                </MuiContainer>

                {this.renderGraph()}

                <ParticipationKnowledgeTestList
                    {...this.props}
                />
            </>
        );
    }
}

////////////////////////////////////////////////////////////////////////////////////////

interface IListState {
    dialog: any;
    data: PagedResult<any>;
    offset: number;
    loading: boolean;
    listOption: DashboardUserAndGroupsParticipationKnowledgeDetailColumn;
    tabOption: ParticipationListPublicationPeriod;
}

interface IListProps extends RenderUserModelListEvents, RenderTopicModelListEvents {
    filter: DashboardUserAndGroupsParticipationBaseFilter;
}

type ListProps = IListProps & WithTranslation;

class ParticipationKnowledgeTestList extends React.PureComponent<ListProps, IListState> {

    constructor(props: ListProps) {
        super(props);

        this.state = {
            dialog: null,
            data: null,
            offset: null,
            loading: true,
            listOption: DashboardUserAndGroupsParticipationKnowledgeDetailColumn.UserMoreAnswers,
            tabOption: ParticipationListPublicationPeriod.Last30
        };
    }

    ///
    private loadData(offset: number) {

        this.setState({ loading: true });

        api.getDashboardUserAndGroupsParticipationKnowledgeTest(
            {
                ...this.getDateFilter(),
                detailColumn: this.state.listOption,
                range: { offset, take: DefaultListParameters.viewRecordsByPage },
                ...this.props.filter
            })
            .then((result) => {
                this.setState({ data: result, loading: false, offset: offset });
            })
            .catch((error) => {
                this.setState({ data: null, loading: false, offset: 0 });
            });
    }

    ///
    private getDateFilter() {

        var lastDay = moment().utc().endOf("day");
        var firstDay = moment().utc().startOf("day");
        if (this.state.tabOption == ParticipationListPublicationPeriod.Last30)
            firstDay.add(-29, "days");
        else
            firstDay = null;

        return {
            startDate: firstDay?.toDate(),
            endDate: lastDay?.toDate()
        };
    }

    ///
    private handleListOption = (e: SelectChangeEvent<DashboardUserAndGroupsParticipationKnowledgeDetailColumn>) => {
        this.setState({ listOption: e.target.value as DashboardUserAndGroupsParticipationKnowledgeDetailColumn }, () => this.loadData(0));
    };

    ///
    private getListOptions() {
        var arr = [];

        if (this.props.filter.memberId == null) {
            arr.push({ key: DashboardUserAndGroupsParticipationKnowledgeDetailColumn.UserMoreAnswers, text: this.props.t("Views.Dashboards.UserAndGroups.KnowledgeTestList.ListOp1") });
        }
        if (this.props.filter.topicId == null) {
            arr.push({ key: DashboardUserAndGroupsParticipationKnowledgeDetailColumn.TopicMoreAnswers, text: this.props.t("Views.Dashboards.UserAndGroups.KnowledgeTestList.ListOp2") });
            arr.push({ key: DashboardUserAndGroupsParticipationKnowledgeDetailColumn.TopicMoreTrust, text: this.props.t("Views.Dashboards.UserAndGroups.KnowledgeTestList.ListOp3") });
        }
        return arr;
    }

    ///
    private renderTitle() {

        return (
            <MuiContainer margin="dense" >
                <Grid container justifyContent="space-between" alignItems="center">
                    <Grid item>

                        <Select
                            value={this.state.listOption}
                            onChange={this.handleListOption}
                        >
                            {
                                this.getListOptions().map((v, index) => <MenuItem key={index} value={v.key}>{v.text}</MenuItem>)
                            }
                        </Select>

                    </Grid>
                    <Grid item>

                        <AppTabs
                            value={this.state.tabOption}
                            onChange={
                                (e, newValue: ParticipationListPublicationPeriod) => {
                                    this.setState({ tabOption: newValue }, () => this.loadData(0));
                                }
                            }
                            indicatorColor="primary"
                            textColor="primary"
                        >
                            <Tab label={this.props.t<string>("Views.Dashboards.Common.LastMonth")} value={ParticipationListPublicationPeriod.Last30} />
                            <Tab label={this.props.t<string>("Views.Dashboards.Common.FullPeriod")} value={ParticipationListPublicationPeriod.All} />
                        </AppTabs>

                    </Grid>
                </Grid>
            </MuiContainer>
        );
    }

    ///
    private renderList() {
        const { data: { items, total }, offset, dialog } = this.state;


        return (
            <MuiContainer margin="normal">
                <>
                    {dialog}
                    <br />
                    {this.renderTitle()}

                    {
                        total == 0
                            ? (
                                <MuiMessageBox variant="warning" message={this.props.t("Views.Dashboards.UserAndGroups.KnowledgeTestList.EmptyData")} />
                            )
                            : (
                                <>
                                    <MuiContainer>
                                        <Table size="small" >
                                            <TableBody>
                                                {
                                                    this.state.listOption == DashboardUserAndGroupsParticipationKnowledgeDetailColumn.UserMoreAnswers
                                                        ? <RenderUserTestModelList indexOffset={offset} items={items} onSelectUser={this.props.onSelectUser} />
                                                        : this.state.listOption == DashboardUserAndGroupsParticipationKnowledgeDetailColumn.TopicMoreAnswers
                                                            ? <RenderTopicTestModelList indexOffset={offset} items={items} onSelectTopic={this.props.onSelectTopic} />
                                                            : <RenderTopicModelList indexOffset={offset} items={items} onSelectTopic={this.props.onSelectTopic} />
                                                }
                                            </TableBody>
                                        </Table>
                                    </MuiContainer>
                                    <MuiPagination
                                        limit={DefaultListParameters.viewRecordsByPage}
                                        offset={this.state.offset}
                                        total={total}
                                        onClick={(e, offset) => this.loadData(offset)}
                                    />
                                </>
                            )
                    }
                </>
            </MuiContainer >
        );
    }

    ///
    private updateListOption() {
        let listOption = this.state.listOption;
        const tiles = this.getListOptions();
        if (tiles.findIndex(p => p.key == listOption) == -1) listOption = tiles[0].key;

        this.setState({ listOption }, () => this.loadData(0));
    }

    ///
    private checkForUpdateState(prevProps: ListProps) {
        if (!LibUtils.isEqual(this.props.filter, prevProps.filter)) {
            this.updateListOption();
        }
    }

    ///
    public componentDidMount() {
        this.updateListOption();
    }

    ///
    public componentDidUpdate(prevProps: ListProps) {
        this.checkForUpdateState(prevProps);
    }

    ///
    public render() {

        if (this.state.loading)
            return <SpinnerIndicator />;

        if (this.state.data == null)
            return <AppMessageBox resource="LoadListDataFailed" />;

        return this.renderList();
    }
}